/**
 * StepFooter (BLU-1376, handover 4.1): the ONE action row at the foot of a
 * step pane.
 *
 * Left: the hint, then the quiet manual overrides (ghost buttons). "Mark as
 * done" shows while the step is not complete, "Undo" while it is complete ONLY
 * through the manual flag, and nothing once the server confirms the step
 * (nothing to undo then). "Skip" shows
 * while the step is not done, "Undo skip" once skipped.
 * Right: the step's own action. Once the step is complete and there is a next
 * step, "Next: <label>" sits beside it and takes the primary look; the step's
 * own action stays, demoted to secondary (`PaneActionToneContext`), so the row
 * always has ONE visually primary button.
 *
 * The overrides render only when the page supplies them in the chrome (it
 * omits them for a caller who cannot write). Everything wraps (`flex-wrap`),
 * so nothing scrolls sideways on a phone.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Ban, CheckCircle2, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isSettledState } from '../../lib/step-state';
import { useStepChrome } from './StepChrome';
import { PANE_PRIMARY, PaneActionToneContext } from './pane-action';

interface StepFooterProps {
  hint?: ReactNode;
  primary?: ReactNode;
}

const GHOST = 'h-8 gap-1.5 px-3 text-xs text-muted-foreground';

export function StepFooter({ hint, primary }: StepFooterProps) {
  const { t } = useTranslation('onboarding');
  const { markDone, skip, next, state } = useStepChrome();

  const markDoneButton = (() => {
    if (!markDone) return null;
    const disabled = markDone.disabled || markDone.pending;
    if (markDone.manuallyDone) {
      return (
        <Button type="button" variant="ghost" size="sm" className={GHOST} disabled={disabled} aria-busy={markDone.pending || undefined} onClick={markDone.onUndo}>
          <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
          {t('footer.undo')}
        </Button>
      );
    }
    if (!markDone.complete) {
      return (
        <Button type="button" variant="ghost" size="sm" className={GHOST} disabled={disabled} aria-busy={markDone.pending || undefined} onClick={markDone.onDone}>
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
          {t('footer.markDone')}
        </Button>
      );
    }
    return null;
  })();

  const skipButton = (() => {
    if (!skip) return null;
    const disabled = skip.disabled || skip.pending;
    if (skip.skipped) {
      return (
        <Button type="button" variant="ghost" size="sm" className={GHOST} disabled={disabled} aria-busy={skip.pending || undefined} onClick={skip.onUnskip}>
          <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
          {t('footer.undoSkip')}
        </Button>
      );
    }
    if (state !== 'done') {
      return (
        <Button type="button" variant="ghost" size="sm" className={GHOST} disabled={disabled} aria-busy={skip.pending || undefined} onClick={skip.onSkip}>
          <Ban className="h-3.5 w-3.5" aria-hidden="true" />
          {t('footer.skip')}
        </Button>
      );
    }
    return null;
  })();

  const showNext = next != null && isSettledState(state);
  const nextButton = showNext && next ? (
    <Button type="button" size="sm" data-tone="primary" className={PANE_PRIMARY} onClick={next.onGo}>
      {t('footer.next', { label: next.label })}
      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
    </Button>
  ) : null;

  const right =
    primary != null || nextButton != null ? (
      <>
        {primary != null && (
          <PaneActionToneContext.Provider value={showNext ? 'secondary' : 'primary'}>
            {primary}
          </PaneActionToneContext.Provider>
        )}
        {nextButton}
      </>
    ) : null;

  const hasLeft = hint != null || markDoneButton != null || skipButton != null;
  if (!hasLeft && right == null) return null;

  return (
    <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-4">
      {hasLeft && (
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          {hint != null && <div className="min-w-0 max-w-prose text-xs text-muted-foreground">{hint}</div>}
          {markDoneButton}
          {skipButton}
        </div>
      )}
      {right != null && <div className="ml-auto flex flex-wrap items-center justify-end gap-2">{right}</div>}
    </div>
  );
}
